export const AR_FEE_TYPE = {
  PER_ANNUAL_RETURN: 1,
  PER_MEMBER: 2,
  PER_INITIATE: 3,
  PER_EXALTEE: 4,
  PER_JOINER: 5,
  PER_RE_JOINER: 6,
  PER_FOREIGN_JOINER: 7,
  PER_DISPENSATION: 8,
} as const;

export type ArFeeType = (typeof AR_FEE_TYPE)[keyof typeof AR_FEE_TYPE];

export const AR_FEE_TYPE_LABELS = {
  [AR_FEE_TYPE.PER_ANNUAL_RETURN]: 'Per Annual Return',
  [AR_FEE_TYPE.PER_MEMBER]: 'Per Member',
  [AR_FEE_TYPE.PER_INITIATE]: 'Per Initiate',
  [AR_FEE_TYPE.PER_EXALTEE]: 'Per Exaltee',
  [AR_FEE_TYPE.PER_JOINER]: 'Per Joiner',
  [AR_FEE_TYPE.PER_RE_JOINER]: 'Per Re-Joiner',
  [AR_FEE_TYPE.PER_FOREIGN_JOINER]: 'Per Foreign Joiner',
  [AR_FEE_TYPE.PER_DISPENSATION]: 'Per Dispensation',
} as const satisfies Record<ArFeeType, string>;

export const AR_FEE_TYPE_OPTIONS = [
  {
    label: AR_FEE_TYPE_LABELS[AR_FEE_TYPE.PER_ANNUAL_RETURN],
    value: AR_FEE_TYPE.PER_ANNUAL_RETURN,
  },
  {
    label: AR_FEE_TYPE_LABELS[AR_FEE_TYPE.PER_MEMBER],
    value: AR_FEE_TYPE.PER_MEMBER,
  },
  {
    label: AR_FEE_TYPE_LABELS[AR_FEE_TYPE.PER_INITIATE],
    value: AR_FEE_TYPE.PER_INITIATE,
  },
  {
    label: AR_FEE_TYPE_LABELS[AR_FEE_TYPE.PER_EXALTEE],
    value: AR_FEE_TYPE.PER_EXALTEE,
  },
  {
    label: AR_FEE_TYPE_LABELS[AR_FEE_TYPE.PER_JOINER],
    value: AR_FEE_TYPE.PER_JOINER,
  },
  {
    label: AR_FEE_TYPE_LABELS[AR_FEE_TYPE.PER_RE_JOINER],
    value: AR_FEE_TYPE.PER_RE_JOINER,
  },
  {
    label: AR_FEE_TYPE_LABELS[AR_FEE_TYPE.PER_FOREIGN_JOINER],
    value: AR_FEE_TYPE.PER_FOREIGN_JOINER,
  },
  {
    label: AR_FEE_TYPE_LABELS[AR_FEE_TYPE.PER_DISPENSATION],
    value: AR_FEE_TYPE.PER_DISPENSATION,
  },
] as const;

const PER_ANNUAL_RETURN_FEE_TYPES: ReadonlySet<ArFeeType> = new Set([
  AR_FEE_TYPE.PER_ANNUAL_RETURN,
  AR_FEE_TYPE.PER_DISPENSATION,
]);

const MEMBER_FEE_TYPES: ReadonlySet<ArFeeType> = new Set([
  AR_FEE_TYPE.PER_MEMBER,
  AR_FEE_TYPE.PER_INITIATE,
  AR_FEE_TYPE.PER_EXALTEE,
  AR_FEE_TYPE.PER_JOINER,
  AR_FEE_TYPE.PER_RE_JOINER,
  AR_FEE_TYPE.PER_FOREIGN_JOINER,
]);

// Fee types whose summary line can drill down to the list of members it applies
// to. Per Annual Return (1) and Per Dispensation (8) are charged against the
// return itself rather than individual members, so they are excluded.
const MEMBER_LIST_FEE_TYPES: ReadonlySet<ArFeeType> = new Set([
  AR_FEE_TYPE.PER_MEMBER,
  AR_FEE_TYPE.PER_INITIATE,
  AR_FEE_TYPE.PER_EXALTEE,
  AR_FEE_TYPE.PER_JOINER,
  AR_FEE_TYPE.PER_RE_JOINER,
  AR_FEE_TYPE.PER_FOREIGN_JOINER,
]);

export function resolveArFeeType(value: number | string | null | undefined) {
  if (value == null) return { label: '—' };
  const feeType = Number(value) as ArFeeType;
  const label = AR_FEE_TYPE_LABELS[feeType];
  return { label: label ?? '—' };
}

export function isPerAnnualReturnFeeType(
  value: number | string | null | undefined,
): boolean {
  if (value == null) return false;
  return PER_ANNUAL_RETURN_FEE_TYPES.has(Number(value) as ArFeeType);
}

export function isMemberFeeType(
  value: number | string | null | undefined,
): boolean {
  if (value == null) return false;
  return MEMBER_FEE_TYPES.has(Number(value) as ArFeeType);
}

/**
 * Whether a fee type's summary line can drill down to the members it applies to.
 * True for the per-member fee types (2–7); false for Per Annual Return (1) and
 * Per Dispensation (8), which are not charged against individual members.
 */
export function hasMemberList(
  value: number | string | null | undefined,
): boolean {
  if (value == null) return false;
  return MEMBER_LIST_FEE_TYPES.has(Number(value) as ArFeeType);
}
